/**
 * Service layer for UI Toast.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, signal } from '@angular/core';

import { getApiErrorMessage } from '@shared/utils';
import { UiToastState, UiToastType } from './ui-toast.types';

/**
 * SERVICE: Ui Toast
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class UiToastService {
  readonly toast = signal<UiToastState | null>(null);

  /**
   * BUSINESS LOGIC: show
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  show(type: UiToastType, title: string, message: string, durationMs = 3000): void {
    this.toast.set({ type, title, message, durationMs });
  }

  /**
   * BUSINESS LOGIC: success
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  success(title: string, message: string, durationMs?: number): void {
    this.show('success', title, message, durationMs);
  }

  /**
   * BUSINESS LOGIC: info
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  info(title: string, message: string, durationMs?: number): void {
    this.show('info', title, message, durationMs);
  }

  /**
   * BUSINESS LOGIC: warning
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  warning(title: string, message: string, durationMs?: number): void {
    this.show('warning', title, message, durationMs);
  }

  /**
   * BUSINESS LOGIC: error
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  error(title: string, message: string, durationMs?: number): void {
    this.show('error', title, message, durationMs);
  }

  /**
   * BUSINESS LOGIC: apiSuccess
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  apiSuccess(title = 'uiLabels.success', message = 'shared.toasts.savedSuccessfully', durationMs?: number): void {
    this.success(title, message, durationMs);
  }

  apiFailure(
    title = 'uiLabels.error',
    err?: unknown,
    fallback = 'shared.toasts.genericError',
    durationMs?: number
  ): void {
    this.error(title, getApiErrorMessage(err, fallback), durationMs);
  }

  /**
   * BUSINESS LOGIC: validationWarning
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  validationWarning(title = 'uiLabels.validation.warning', message = 'shared.validation.completeRequired', durationMs?: number): void {
    this.warning(title, message, durationMs);
  }

  /**
   * BUSINESS LOGIC: created
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  created(entityName: string, message?: string): void {
    this.success(entityName, message ?? 'shared.toasts.created');
  }

  /**
   * BUSINESS LOGIC: updated
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  updated(entityName: string, message?: string): void {
    this.success(entityName, message ?? 'shared.toasts.updated');
  }

  /**
   * BUSINESS LOGIC: deleted
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  deleted(entityName: string, message?: string): void {
    this.success(entityName, message ?? 'shared.toasts.deleted');
  }

  /**
   * BUSINESS LOGIC: exported
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  exported(entityName: string, message?: string): void {
    this.success(entityName, message ?? 'shared.toasts.exported');
  }

  /**
   * BUSINESS LOGIC: dismiss
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  dismiss(): void {
    this.toast.set(null);
  }
}
